import { useEffect, useMemo, useState } from "react";

import { logout } from "../../auth";
import { get, post } from "../../services/api";
import AdminSection from "../../components/AdminSection/AdminSection";
import NotifierPopup from "../../components/NotifierPopup/NotifierPopup";

import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import "./QuadranteMetaMesVend.css";

const NOMES_MESES = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
];

function construirPeriodosPermitidos() {
    const hoje = new Date();
    const offsets = [-1, 0, 1];

    return offsets.map((offset) => {
        const data = new Date(hoje.getFullYear(), hoje.getMonth() + offset, 1);
        const mes = data.getMonth() + 1;
        const ano = data.getFullYear();

        return {
            value: `${mes}-${ano}`,
            label: `${NOMES_MESES[mes - 1]}/${ano}`,
            mes,
            ano,
        };
    });
}

function formatarNumeroBR(valor) {
    return Number(valor || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function parseNumeroBR(texto) {
    if (!texto) {
        return NaN;
    }

    const normalizado = String(texto).replace(/\./g, "").replace(",", ".").trim();
    return Number(normalizado);
}

export default function QuadranteMetaMesVend() {
    const periodosPermitidos = useMemo(() => construirPeriodosPermitidos(), []);
    const [periodoSelecionado, setPeriodoSelecionado] = useState(periodosPermitidos[1]?.value || periodosPermitidos[0]?.value || "");
    const [meta, setMeta] = useState("");
    const [loadingConsulta, setLoadingConsulta] = useState(false);
    const [loadingSalvar, setLoadingSalvar] = useState(false);
    const [notify, setNotify] = useState({
        isActive: false,
        isSuccessful: undefined,
        message: undefined,
    });

    const periodoAtual = useMemo(
        () => periodosPermitidos.find((periodo) => periodo.value === periodoSelecionado) || periodosPermitidos[0],
        [periodoSelecionado, periodosPermitidos],
    );

    async function consultarMeta() {
        const authToken = localStorage.getItem("adminToken");

        if (!authToken) {
            logout();
            return;
        }

        setLoadingConsulta(true);

        try {
            const response = await get(`/admin/quadrante-excelencia/meta?mes=${periodoAtual.mes}&ano=${periodoAtual.ano}`, null, authToken);
            const valorMeta = Number(response?.meta ?? 0);
            setMeta(formatarNumeroBR(valorMeta));
        } catch (error) {
            setNotify({
                isActive: true,
                isSuccessful: false,
                message: "Não foi possível consultar a meta.",
            });
        } finally {
            setLoadingConsulta(false);
        }
    }

    useEffect(() => {
        consultarMeta();
    }, []);

    async function salvarMeta(event) {
        event.preventDefault();

        const authToken = localStorage.getItem("adminToken");

        if (!authToken) {
            logout();
            return;
        }

        const metaNumerica = parseNumeroBR(meta);

        if (Number.isNaN(metaNumerica) || metaNumerica < 0) {
            setNotify({
                isActive: true,
                isSuccessful: false,
                message: "Informe uma meta válida.",
            });
            return;
        }

        setLoadingSalvar(true);

        try {
            const response = await post(
                "/admin/quadrante-excelencia/meta",
                { mes: periodoAtual.mes, ano: periodoAtual.ano, meta: metaNumerica },
                authToken,
                { headers: { "Content-Type": "application/json" } },
            );

            if (response?.response?.status >= 400) {
                setNotify({
                    isActive: true,
                    isSuccessful: false,
                    message: response?.response?.data?.msg || "Não foi possível salvar a meta.",
                });
                return;
            }

            setMeta(formatarNumeroBR(metaNumerica));
            setNotify({
                isActive: true,
                isSuccessful: true,
                message: "Meta do quadrante atualizada com sucesso.",
            });
        } catch (error) {
            setNotify({
                isActive: true,
                isSuccessful: false,
                message: "Não foi possível salvar a meta.",
            });
        } finally {
            setLoadingSalvar(false);
        }
    }

    return (
        <>
            {notify.isActive && (
                <NotifierPopup
                    notify={notify}
                    setNotify={setNotify}
                />
            )}

            <AdminSection
                etapa={<TuneRoundedIcon style={{ width: "30px", height: "30px" }} />}
                titulo={"Meta mensal do quadrante"}
                contexto={"Defina o valor da meta para o mês/ano no WebVendas."}
            >
                <form className="quadrante-meta-form" onSubmit={salvarMeta}>
                    <div className="quadrante-meta-fields">
                        <div className="quadrante-meta-field">
                            <label htmlFor="periodo" className="admin-label">Período</label>
                            <select
                                id="periodo"
                                value={periodoSelecionado}
                                onChange={(event) => setPeriodoSelecionado(event.target.value)}
                            >
                                {periodosPermitidos.map((periodo) => (
                                    <option key={periodo.value} value={periodo.value}>{periodo.label}</option>
                                ))}
                            </select>
                        </div>

                        <button
                            type="button"
                            className="section-button"
                            onClick={consultarMeta}
                            disabled={loadingConsulta}
                        >
                            {loadingConsulta ? "Consultando..." : "Consultar"}
                        </button>
                    </div>

                    <div className="quadrante-meta-input-group">
                        <label htmlFor="meta" className="admin-label">Meta</label>
                        <input
                            id="meta"
                            type="text"
                            value={meta}
                            onChange={(event) => setMeta(event.target.value)}
                            placeholder="0,00"
                        />
                        <span className="quadrante-meta-hint">Use ponto para milhar e vírgula para decimal.</span>
                    </div>

                    <div className="quadrante-meta-actions">
                        <button
                            type="submit"
                            className="animal-save-btn"
                            disabled={loadingSalvar}
                        >
                            {loadingSalvar ? "Salvando..." : "Salvar meta"}
                        </button>
                    </div>
                </form>
            </AdminSection>
        </>
    );
}